import { useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { GuidService } from 'ui-components/services/guid-service';

import ModalPortal from '../../../../../components/modal-portal';
import { TemplateType } from '../../../../../components/html-editor/types';
import { useDeleteHtmlTemplate } from '../../../../../components/html-editor/use-html-templates';
import { messages } from '../../messages';
import styles from './delete-pick-list-template.module.less';
import { Text, TextType } from 'ui-components/components/text';
import { FlexRow, Gap } from 'ui-components/components/flex';

type DeletePickListTemplateProps = {
  templateId: string;
  templateName: string;
  onCancel: () => void;
  onDeleted: () => void;
};

export const DeletePickListTemplate = ({ templateId, templateName, onCancel, onDeleted }: DeletePickListTemplateProps) => {
  const modalHeaderId = useRef(GuidService.newGuid()).current;
  const { mutate: deleteTemplate, isPending } = useDeleteHtmlTemplate(TemplateType.PickList);

  const handleConfirmDelete = useCallback(() => {
    deleteTemplate(templateId, {
      onSuccess: onDeleted,
      onError: () => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.deleteError} />,
          dontHide: true,
        });
      },
    });
  }, [deleteTemplate, templateId, onDeleted]);

  return (
    <ModalPortal onClose={onCancel}>
      <Modal modalHeaderId={modalHeaderId} size='small'>
        <ModalHeader>
          <FormattedMessage {...messages.deleteConfirmationTitle} />
        </ModalHeader>
        <ModalBody className={styles.modalBody}>
          <FlexRow gap={Gap.Space16}>
            <Text textType={TextType.H4} value={messages.askToDelete} />
            <Text textType={TextType.Description} value={templateName} />
          </FlexRow>
        </ModalBody>
        <ModalFooter>
          <ModalFooterCancelButton />
          <Button disabled={isPending} isLoading={isPending} onClick={handleConfirmDelete} theme={THEME_CONSTANTS.danger}>
            <FormattedMessage {...messages.deleteButton} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
